if (!customElements.get('featured-video-swiper-popup')) {
    customElements.define(
        'featured-video-swiper-popup',
        class FeaturedVideoSwiperPopup extends HTMLElement {
            constructor() {
                super();
                this.swiper = null;
                this.VidstackPlayer = null;
                this.VidstackPlayerLayout = null;
                this.inViewFlag = false;
                this.resourcesLoaded = false;
                this.loadingRequested = false;
                this.mainVideoPlayed = false;

                this.elements = {
                    swiperContainer: this.querySelector('.swiper'),
                    swiperSlides: this.querySelectorAll('.swiper-slide'),
                    mainVideo: this.querySelector('.featured-video-swiper-popup__video-main-video-target'),
                    thumbVideoButtons: this.querySelectorAll('.featured-video-swiper-popup__video-item-poster'),
                    dialogContainer: this.querySelector('.swiper-dialog-video__dialog'),
                    dialogCloseButton: this.querySelector('.swiper-dialog-video__dialog-close-mark'),
                    mediaElements: this.querySelector('.swiper-dialog-video__dialog-video'),
                };
            }

            connectedCallback() {
                this.scheduleIdleLoad();
                this.inView();
                this.initSwiper();

                this.elements.swiperSlides.forEach(slide => {
                    slide.addEventListener('click', () => this.onThumbVideoButtonClick(slide));
                });
                this.elements.dialogCloseButton.addEventListener('click', () => this.dialogHide());
            }

            scheduleIdleLoad() {
                if (this.loadingRequested || this.resourcesLoaded || this.mainVideoPlayed) return; // Check the new flag

                this.loadingRequested = true;

                if ('requestIdleCallback' in window) {
                    requestIdleCallback(
                        () => this.loadVidstackResources().then(success => {
                            if (success && !this.inViewFlag && !this.mainVideoPlayed) { // Check the new flag
                                this.playMainVideo();
                            }
                        }),
                        { timeout: 2000 }
                    );
                } else {
                    this.loadVidstackResources().then(success => {
                        if (success && !this.inViewFlag && !this.mainVideoPlayed) { // Check the new flag
                            this.playMainVideo();
                        }
                    });
                }
            }

            // 完整的initSwiper方法
            initSwiper() {
                if (!this.elements.swiperContainer) return;

                const prevButton = this.querySelector('.swiper-button-prev');
                const nextButton = this.querySelector('.swiper-button-next');

                this.swiper = new Swiper(this.elements.swiperContainer, {
                    slidesPerView: 4,
                    spaceBetween: 24,
                    navigation: {
                        nextEl: nextButton || null,
                        prevEl: prevButton || null,
                    },
                    breakpoints: {
                        320: {
                            spaceBetween: 8,
                            slidesPerView: 2,
                        },
                        750: {
                            spaceBetween: 16,
                            slidesPerView: 2,
                        },
                        1100: {
                            spaceBetween: 24,
                            slidesPerView: 3,
                        },
                        1441: {
                            spaceBetween: 24,
                            slidesPerView: 4,
                        }
                    }
                });
            }

            // 完整的playMainVideo方法
            async playMainVideo() {
                if (!this.VidstackPlayer || !this.VidstackPlayerLayout || !this.elements.mainVideo || this.mainVideoPlayed) { // Check the new flag
                    return;
                }

                const config = {
                    src: this.elements.mainVideo.getAttribute('data-src'),
                    poster: this.elements.mainVideo.getAttribute('data-full-poster'),
                    title: this.elements.mainVideo.getAttribute('data-title')
                };

                await this.createVideoPlayer(this.elements.mainVideo, config, true);
                this.mainVideoPlayed = true; // Set the flag after playing
            }

            inView() {
                const observer = new IntersectionObserver(async (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting && !this.inViewFlag) {
                            this.inViewFlag = true;

                            if (!this.resourcesLoaded) {
                                const success = await this.loadVidstackResources();
                                if (success && !this.mainVideoPlayed) { // Check the new flag
                                    this.playMainVideo();
                                }
                            } else if (!this.mainVideoPlayed) { // Check the new flag
                                this.playMainVideo();
                            }

                            observer.disconnect();
                        }
                    }
                }, {
                    rootMargin: '100px',
                    threshold: 0.1
                });

                observer.observe(this);
            }

            async loadVidstackResources() {
                if (this.resourcesLoaded) return true;

                try {
                    const configElement = document.querySelector('.vidstack-config');
                    if (!configElement) throw new Error('Vidstack config element not found');

                    const config = JSON.parse(configElement.textContent.trim());
                    const themeConfig = config.theme?.vidstack;
                    if (!themeConfig) throw new Error('Invalid Vidstack configuration');

                    // 加载CSS
                    const loadCSS = (url) => {
                        if (!url) return;
                        const link = document.createElement('link');
                        link.rel = 'stylesheet';
                        link.href = url;
                        document.head.appendChild(link);
                    };

                    loadCSS(themeConfig.css?.player);
                    loadCSS(themeConfig.css?.theme);

                    // 预加载JS
                    const jsUrl = themeConfig.js?.[0];
                    if (jsUrl) {
                        const preloadJS = document.createElement('link');
                        preloadJS.rel = 'modulepreload';
                        preloadJS.href = jsUrl;
                        document.head.appendChild(preloadJS);
                    }

                    if (!jsUrl) throw new Error('No JS URL found in configuration');

                    const { VidstackPlayer, VidstackPlayerLayout } = await import(jsUrl);
                    this.VidstackPlayer = VidstackPlayer;
                    this.VidstackPlayerLayout = VidstackPlayerLayout;

                    this.resourcesLoaded = true;
                    return true;
                } catch (error) {
                    console.error('Failed to load Vidstack resources:', error);
                    return false;
                }
            }

            dialogShow() {
                this.elements.dialogContainer.setAttribute('open', '');
            }

            dialogHide() {
                this.elements.dialogContainer.removeAttribute('open');
                if (this.elements.mediaElements) {
                    this.elements.mediaElements.innerHTML = '';
                }
            }

            async createVideoPlayer(target, config, autoplay = false) {
                if (!this.VidstackPlayer || !this.VidstackPlayerLayout) return null;

                try {
                    const layout = new this.VidstackPlayerLayout({ thumbnails: null });
                    return await this.VidstackPlayer.create({
                        target,
                        src: config.src,
                        viewType: 'video',
                        streamType: 'on-demand',
                        logLevel: 'warn',
                        muted: true,
                        crossOrigin: true,
                        playsInline: true,
                        poster: config.poster,
                        autoplay,
                        layout,
                        title: config.title
                    });
                } catch (error) {
                    console.error('Failed to create video player:', error);
                    return null;
                }
            }

            onThumbVideoButtonClick(slide) {
                this.dialogShow();
                const target = this.elements.mediaElements;
                const config = {
                    src: slide.getAttribute('data-src'),
                    poster: slide.getAttribute('data-full-poster'),
                    title: slide.getAttribute('data-title')
                };
                this.createVideoPlayer(target, config, true);
            }

            disconnectedCallback() {
                this.VidstackPlayer = null;
                this.VidstackPlayerLayout = null;
            }
        }
    );
}